import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { rsvpApi } from "../../api/rsvp";
import { PublicTierSignInForm } from "../../components/events/PublicTierSignInForm";
import { PublicTicketTemplateShell } from "../../components/ticketTemplate/PublicTicketTemplateShell";
import "../../components/ticketTemplate/ticketTemplate.css";
import "../../components/ticketTemplate/zoniqTicketTemplate.css";
import { queryKeys } from "../../queryKeys";
import { DEFAULT_TICKET_TEMPLATE_CONFIG } from "../../types/rsvp";
import type { PayPreference, RsvpGuest } from "../../types/rsvp";
import { findSettingsByTokenLocal } from "../../utils/rsvpStore";
import { mergeTemplateConfig } from "../../utils/ticketTemplateStore";

const PublicRsvpPage: React.FC = () => {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const [done, setDone] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [result, setResult] = useState<RsvpGuest | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const metaQuery = useQuery({
    queryKey: inviteToken ? queryKeys.publicRsvp(inviteToken) : ["rsvp", "public", "idle"],
    queryFn: async () => {
      const meta = await rsvpApi.publicMeta(inviteToken!);
      if (meta.link_kind === "guest_list") {
        throw new Error("This RSVP link is invalid or has expired.");
      }
      if (!meta.link_kind) {
        const settings = findSettingsByTokenLocal(inviteToken!);
        if (settings && settings.invite_token !== inviteToken) {
          throw new Error("This RSVP link is invalid or has expired.");
        }
      }
      return meta;
    },
    enabled: Boolean(inviteToken),
    retry: 1,
  });

  const submitMutation = useMutation({
    mutationFn: async (values: {
      name: string;
      phone: string;
      email?: string;
      tier_counts: Record<string, number>;
      party_size: number;
      promoter?: string;
      promoter_id?: string;
      whatsapp_opt_in: boolean;
      pay_preference?: PayPreference;
    }) => {
      if (!inviteToken) throw new Error("Missing invite");
      return rsvpApi.publicSubmit(inviteToken, {
        name: values.name,
        phone: values.phone,
        email: values.email ?? null,
        party_size: values.party_size,
        tier_counts: values.tier_counts,
        category: "stag",
        promoter: values.promoter,
        promoter_id: values.promoter_id,
        source: "rsvp",
        whatsapp_opt_in: values.whatsapp_opt_in,
        pay_preference: values.pay_preference,
      });
    },
    onSuccess: (data, values) => {
      setGuestName(values.name);
      setResult(data);
      if (data.pay_url) {
        // Paid online: hand the guest to the secure PayU page; they come back to their status page.
        setRedirecting(true);
        window.location.assign(data.pay_url);
        return;
      }
      setDone(true);
      toast.success("You're on the list");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not submit RSVP"),
  });

  if (!inviteToken) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Invalid RSVP link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (metaQuery.isPending) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Loading event…</p>
          </div>
        </div>
      </div>
    );
  }

  if (metaQuery.isError || !metaQuery.data) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body zticket-status">
            <h2 className="zticket-status__title">Link unavailable</h2>
            <p className="zticket-status__text">
              {metaQuery.error instanceof Error
                ? metaQuery.error.message
                : "This RSVP link is invalid or has expired."}
            </p>
            <Link to="/" className="eventos-nav-btn">
              Go to Zoniq
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const meta = metaQuery.data;
  const templateId = meta.ticket_template_id ?? "black";
  const templateConfig = mergeTemplateConfig(meta.template_config ?? DEFAULT_TICKET_TEMPLATE_CONFIG);

  if (redirecting) {
    return (
      <PublicTicketTemplateShell templateId={templateId} config={templateConfig} meta={meta} eyebrow="RSVP">
        <div className="zticket-status">
          <h2 className="zticket-status__title">Taking you to payment…</h2>
          <p className="zticket-status__text">Please don&apos;t close this page.</p>
        </div>
      </PublicTicketTemplateShell>
    );
  }

  if (done) {
    const payAtGate = result?.payment_status === "due";
    return (
      <PublicTicketTemplateShell
        templateId={templateId}
        config={templateConfig}
        meta={meta}
        eyebrow="RSVP"
      >
        <div className="zticket-status">
          <h2 className="zticket-status__title">You&apos;re registered</h2>
          <p className="zticket-status__text">
            Thanks, {guestName || "guest"}. See you at {meta.event_name}.
            {payAtGate
              ? " Your host will confirm your entry; you'll pay at the gate."
              : ""}
          </p>
        </div>
      </PublicTicketTemplateShell>
    );
  }

  return (
    <PublicTicketTemplateShell templateId={templateId} config={templateConfig} meta={meta} eyebrow="RSVP">
      <PublicTierSignInForm
        tiers={meta.ticket_tiers ?? []}
        includePromoter
        promoters={meta.promoters ?? []}
        promoterDisplay={templateConfig.promoter_display}
        showPricing
        paymentMode={meta.payment_mode ?? "free"}
        submitLabel="Get my pass"
        pending={submitMutation.isPending}
        onSubmit={(values) => submitMutation.mutate(values)}
        submitError={submitMutation.error}
      />
    </PublicTicketTemplateShell>
  );
};

export default PublicRsvpPage;
